import { useEffect, useState } from 'react';
import type { ReactElement } from 'react';
import { useI18n } from '@xdocs/ui';
import { useWs } from '../ws/WsProvider.js';
import { createBrowserWsPersistence } from '../ws/browserPersistence.js';
import styles from './OfflineQueueIndicator.module.css';

const POLL_INTERVAL_MS = 2000;

/**
 * Queued-offline-actions count chip (E20 §5.10-4). `@xdocs/ws-protocol`'s
 * `WsClient` has no live "queue size changed" event today — only an
 * `'offline-flushed'` event per item as it drains, nothing on `enqueue`
 * (see `./WIRING.md` for the recommended addition: a `getOfflineQueueSize()`
 * getter or an `'offline-queue-changed'` event). Until that lands, this
 * reads the SAME durable localStorage state the live `WsClient` instance
 * persists to (`apps/web/src/ws/browserPersistence.ts`, key
 * `xdocs.ws-client-state`) via a short poll, refreshed immediately on the
 * connection events that are actually observable (`state` transitions and
 * `offline-flushed`) so the count updates promptly around a reconnect/flush
 * without waiting out the full poll interval.
 *
 * Renders nothing when the queue is empty (E19: not a permanent chrome
 * element, only appears when there's something to say).
 */
export function OfflineQueueIndicator(): ReactElement | null {
  const { wsClient } = useWs();
  const { t } = useI18n();
  const [count, setCount] = useState(0);

  useEffect(() => {
    const persistence = createBrowserWsPersistence();
    let cancelled = false;

    async function refresh(): Promise<void> {
      const state = await persistence.load();
      if (!cancelled) setCount(state.offlineQueue.length);
    }

    void refresh();
    const interval = setInterval(() => void refresh(), POLL_INTERVAL_MS);
    const offState = wsClient.on('state', () => void refresh());
    const offFlushed = wsClient.on('offline-flushed', () => void refresh());

    return () => {
      cancelled = true;
      clearInterval(interval);
      offState();
      offFlushed();
    };
  }, [wsClient]);

  if (count === 0) return null;

  return (
    <span className={styles['chip']} role="status" aria-label={t('pwa:offlineQueue.label')}>
      <span className={styles['dot']} aria-hidden="true" />
      {t('pwa:offlineQueue.pending', { count })}
    </span>
  );
}
